import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { getDoc, doc, getFirestore } from 'firebase/firestore';
import { evaluateArgument } from './openai';
import { 
  updateAnalysisResult, 
  getSavedAnalysisResult,
  updateArgumentAnalysis,
  getStudentArgument,
  getOpponentArgument
} from './firebaseService';

// Firebase 초기화
const functions = getFunctions(getApp(), 'us-central1');
const db = getFirestore(getApp());

// 점수에 따른 설명 생성 함수
export const getScoreExplanation = (type, score) => {
  const percentage = Math.round(score * 10);
  
  switch (type) {
    case 'logic':
      if (percentage >= 80) return '논리적 구조가 매우 체계적이고 명확함';
      if (percentage >= 60) return '논리적 연결이 대체로 적절함';
      if (percentage >= 40) return '논리적 구조에 일부 개선이 필요함';
      return '논리적 연결이 불분명하거나 부족함';
      
    case 'factual':
      if (percentage >= 80) return '사실 기반의 신뢰할 만한 정보 제시';
      if (percentage >= 60) return '대부분 검증 가능한 사실에 기반함';
      if (percentage >= 40) return '일부 사실 확인이 필요한 내용 포함';
      return '사실성 검증이 어려운 내용이 많음';
      
    case 'rebuttal':
      if (percentage >= 80) return '근거가 주장을 강력하게 뒷받침함';
      if (percentage >= 60) return '근거가 주장과 적절히 연결됨';
      if (percentage >= 40) return '근거의 관련성이 다소 부족함';
      return '근거가 주장을 충분히 뒷받침하지 못함';
      
    default:
      return '평가 중...';
  }
};

// AI 평가 텍스트 파싱 함수
export const parseAIEvaluation = (text, isRebuttal = false) => {
  // 반론 분석과 주장 분석의 섹션 구분
  const sections = isRebuttal ? {
    '최종결론': '',
    '핵심 논점 파악 및 반박': '',
    '반박의 논리성 및 근거': '',
    '설득력 및 영향': ''
  } : {
    '최종 결론': '',
    '논리성 분석': '',
    '사실성 검증': '',
    '근거 적절성 평가': ''
  };
  
  // 텍스트 정리 함수 - 특수문자 제거
  const cleanText = (text) => {
    return text
      .replace(/\*\*/g, '') // ** 제거
      .replace(/\*/g, '')   // * 제거
      .replace(/##/g, '')   // ## 제거
      .replace(/#/g, '')    // # 제거
      .replace(/^\s*-\s*/gm, '') // 줄 시작의 "- " 제거
      .trim(); // 앞뒤 공백 제거
  };
  
  // 섹션 제목들을 찾아서 텍스트 분리
  const sectionTitles = isRebuttal ? [
    '최종결론',
    '최종 결론',
    '핵심 논점 파악 및 반박',
    '반박의 논리성 및 근거',
    '설득력 및 영향'
  ] : [
    '최종 결론',
    '논리성 분석',
    '사실성 검증', 
    '근거 적절성 평가'
  ];
  
  let currentSection = '';
  const lines = text.split('\n');
  
  lines.forEach(line => {
    const trimmedLine = line.trim();
    
    // 섹션 제목 찾기 (**, ##, 또는 단독으로 포함된 경우)
    const foundSection = sectionTitles.find(title => 
      trimmedLine.includes(title) && 
      (trimmedLine.startsWith('**') || trimmedLine.startsWith('##') || trimmedLine === title)
    );
    
    if (foundSection) {
      // '최종 결론'과 '최종결론'을 같은 섹션으로 처리
      if (foundSection === '최종 결론' || foundSection === '최종결론') {
        currentSection = isRebuttal ? '최종결론' : '최종 결론';
      } else {
        currentSection = foundSection;
      }
    } else if (currentSection && trimmedLine) {
      // 현재 섹션에 내용 추가 (제목이 아닌 실제 내용만, 특수문자 제거)
      if (!trimmedLine.startsWith('**') && !trimmedLine.startsWith('##')) {
        const cleanedLine = cleanText(trimmedLine);
        if (cleanedLine) { // 정리 후 빈 줄이 아닌 경우만 추가
          sections[currentSection] += (sections[currentSection] ? '\n' : '') + cleanedLine;
        }
      }
    }
  });
  
  return sections;
};

// 저장된 AI 분석 결과 로드 (통합 구조 버전)
export const loadSavedAnalysis = async (roomCode, nickname, position, argumentId, argumentContent, topic, currentRound, opponentArgumentContent) => {
  try {
    // 학생 문서에서 해당 라운드의 주장과 분석 결과 확인
    const result = await getStudentArgument(roomCode, nickname, position, currentRound);
    
    if (result.success && result.argument && result.argument.analysisResult) {
      const savedAnalysis = result.argument.analysisResult;
      
      // [수정] 3라운드(반론) 분석 결과가 이전 포맷(주장 평가)이면 캐시 무시
      if (currentRound === 3 && savedAnalysis.detailedScores && savedAnalysis.detailedScores.hasOwnProperty('factual')) {
        console.log('오래된 3라운드 분석 캐시 발견. AI 재분석을 실행합니다.');
        // 캐시가 유효하지 않으므로 아래의 새 분석 로직으로 넘어감
      } else {
        return {
          success: true,
          analysisResult: savedAnalysis,
          fromCache: true,
        };
      }
    }

    // 저장된 분석 결과가 없거나 유효하지 않으면 새로운 분석 수행
    const evaluation = await evaluateArgument(
      argumentContent,
      topic,
      position,
      currentRound,
      opponentArgumentContent // 상대방 주장 전달
    );
    
    // 분석 결과를 학생 문서의 해당 주장에 저장
    const analysisData = {
      confidenceScore: null, // 단순 분석에서는 점수 없음
      detailedScores: null,
      analysisResult: evaluation
    };
    
    await updateArgumentAnalysis(roomCode, nickname, position, argumentId, analysisData);
    
    
    return {
      success: true,
      analysisResult: evaluation,
      fromCache: false
    };
    
  } catch (error) {
    console.error('AI 평가 오류:', error);
    return {
      success: false,
      error: error.message,
      analysisResult: 'AI 평가를 불러오는 중 오류가 발생했습니다.'
    };
  }
};

// AI 분석 시작 함수 (통합 구조 버전)
export const startAiAnalysisForStudent = async (nickname, position, argumentId, argumentContent, topic, currentRound, roomCode) => {
  try {
    let opponentArgumentContent = '';
    // 3라운드(반론)인 경우, 상대방의 2라운드 주장을 가져옴
    if (currentRound === 3) {
      const opponentResult = await getOpponentArgument(roomCode, nickname, position);
      if (opponentResult.success && opponentResult.argument) {
        opponentArgumentContent = opponentResult.argument.content;
      }
    }
    
    // 1. 상세 AI 분석 수행 (현재 라운드 정보와 상대방 주장 전달)
    const evaluation = await evaluateArgument(
      argumentContent,
      topic,
      position,
      currentRound,
      opponentArgumentContent
    );
    
    
    // 2. 주장/반론 평가 점수 계산
    let processResult;
    if (currentRound === 3) {
      // 3라운드일 경우: 새로운 반론 평가 함수(V2) 호출
      const processDebateRebuttal = httpsCallable(functions, 'processDebateRebuttalV2');
      processResult = await processDebateRebuttal({
        side: position === '찬성' ? 'pro' : 'con',
        rebuttal: argumentContent,
        originalArgument: opponentArgumentContent,
        topic: topic,
      });
    } else {
      // 그 외 라운드: 기존 주장 평가 함수 호출
      const processDebateArgument = httpsCallable(functions, 'processDebateArgument');
      processResult = await processDebateArgument({
        side: position === '찬성' ? 'pro' : 'con',
        argument: argumentContent,
        topic: topic
      });
    }
    
    const evaluationScores = processResult.data?.evaluationScores || null;
    
    
    // 3. 통합 구조로 분석 결과 업데이트
    
    const analysisData = {
      confidenceScore: evaluationScores?.confidenceScore || null,
      detailedScores: evaluationScores?.detailedScores || null,
      analysisResult: evaluation
    };
    
    await updateArgumentAnalysis(roomCode, nickname, position, argumentId, analysisData);
    
    
    
    
    return {
      success: true,
      data: {
        evaluation,
        confidenceScore: evaluationScores?.confidenceScore || null,
        detailedScores: evaluationScores?.detailedScores || null
      }
    };
    
  } catch (error) {
    console.error('AI 분석 오류:', error);
    console.error('오류 상세:', error.message);
    
    // 기본 분석 결과 및 배심원 이동 정보 생성
    const defaultEvaluation = `AI 분석 중 오류가 발생했습니다.\n\n**논리성 분석**\n주장의 논리적 구조를 분석하여 기본적인 평가를 제공합니다. 논리적 연결성을 개선하면 더 좋은 평가를 받을 수 있습니다.\n\n**사실성 검증**\n제시된 정보들의 사실성을 검토하여 일반적인 평가를 제공합니다. 구체적이고 검증 가능한 데이터를 추가하면 신뢰성을 높일 수 있습니다.\n\n**근거 적절성 평가**\n주장을 뒷받침하는 근거들의 적절성을 평가하여 기본적인 피드백을 제공합니다. 보다 구체적이고 관련성 높은 근거를 제시하면 설득력을 강화할 수 있습니다.\n\n**최종 결론**\n기술적 오류로 인해 상세 분석을 완료하지 못했지만, 기본적인 평가 결과를 제공합니다.`;
    
    // 기본 평가 점수 생성 (중립적 평가)
    const defaultScores = {
      confidenceScore: 3.0,
      detailedScores: { logic: 3, factual: 3, rebuttal: 3 }
    };
    
    // Firebase에 기본값 저장 시도
    try {
      const analysisData = {
        confidenceScore: defaultScores.confidenceScore,
        detailedScores: defaultScores.detailedScores,
        analysisResult: defaultEvaluation
      };
      
      await updateArgumentAnalysis(roomCode, nickname, position, argumentId, analysisData);
      
    } catch (firebaseError) {
      console.error('Firebase 기본값 저장 실패:', firebaseError);
    }
    
    
    
    return {
      success: false,
      error: error.message,
      defaultData: {
        evaluation: defaultEvaluation,
        confidenceScore: defaultScores.confidenceScore,
        detailedScores: defaultScores.detailedScores
      }
    };
  }
}; 

/**
 * 명언 해설 생성 (by GPT-4o via Firebase Functions)
 * @param {string} author - 위인 이름
 * @param {string} quote - 명언 내용
 * @returns {Promise<{success: boolean, explanation: string}>}
 */
export const generateSayingExplanation = async ({ author, quote }) => {
  if (!author || !quote) {
    return { success: false, explanation: '위인 이름과 명언 내용이 필요합니다.' };
  }

  try {
    const generateFn = httpsCallable(functions, 'generateSayingExplanation');
    const result = await generateFn({ author, quote });
    
    const data = result.data;
    if (data.success) {
      return { success: true, explanation: data.explanation };
    } else {
      throw new Error(data.explanation || 'AI 응답 생성에 실패했습니다.');
    }
  } catch (error) {
    console.error('Error generating saying explanation:', error);
    const errorMessage = error.message || '해설을 생성하는 중 오류가 발생했습니다.';
    return { success: false, explanation: errorMessage };
  }
};

/**
 * 영어 문법 분석 결과를 파싱하는 함수
 * @param {string} text - OpenAI로부터 받은 문법 분석 결과 텍스트
 * @returns {Object} 섹션별로 분류된 분석 결과
 */
export const parseGrammarAnalysis = (text) => {
  const sections = {};
  
  if (!text) {
    return sections;
  }

  // ### 형식의 헤더 또는 **1. 형식의 헤더를 찾아서 파싱
  const lines = text.split('\n');
  let currentSection = null;
  let currentContent = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // ### 형식의 헤더 감지
    if (line.startsWith('###')) {
      if (currentSection) {
        sections[currentSection] = currentContent.join('\n').trim();
      }
      currentSection = line.replace(/^###\s*/, '').trim();
      currentContent = [];
    }
    // **1. 형식의 헤더 감지 (번호가 있는 경우)
    else if (line.match(/^\*\*\d+\.\s*/)) {
      if (currentSection) {
        sections[currentSection] = currentContent.join('\n').trim();
      }
      currentSection = line.replace(/^\*\*\d+\.\s*/, '').replace(/\*\*$/, '').trim();
      currentContent = [];
    }
    // ** 형식의 헤더 감지 (번호가 없는 경우)
    else if (line.startsWith('**') && line.endsWith('**')) {
      if (currentSection) {
        sections[currentSection] = currentContent.join('\n').trim();
      }
      currentSection = line.replace(/^\*\*/, '').replace(/\*\*$/, '').trim();
      currentContent = [];
    }
    // 헤더가 아닌 경우 현재 섹션의 내용으로 추가
    else if (currentSection && line) {
      currentContent.push(line);
    }
  }

  // 마지막 섹션 저장
  if (currentSection) {
    sections[currentSection] = currentContent.join('\n').trim();
  }

  return sections;
}; 